import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import { CTASection } from '../components/CTASection';
import { Plus, Minus, HelpCircle, Search, ChevronDown } from 'lucide-react';
import { AnimatedGridPattern } from '../components/ui/AnimatedGridPattern';
import { Input } from '../components/ui/Input';
interface FAQPageProps {
  onNavigate: (page: string) => void;
}
export function FAQPage({
  onNavigate
}: FAQPageProps) {
  const [openIndex, setOpenIndex] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const toggleFAQ = (id: string) => {
    setOpenIndex(openIndex === id ? null : id);
  };
  const faqs = [{
    category: 'Allgemein',
    questions: [{
      id: 'q1',
      q: 'Was ist smartprotect24?',
      a: 'Vergleiche Versicherungen digital, behalte den Überblick – und triff am Ende eine sichere Entscheidung. Wenn du möchtest, begleitet dich dabei ein erfahrener Versicherungsmakler, der die Ergebnisse für dich verständlich einordnet und dich bei der Umsetzung unterstützt. Klar, unabhängig und ohne Verkaufsdruck.'
    }, {
      id: 'q2',
      q: 'Wie funktionieren die Rechner?',
      a: 'Die Rechner sind Vergleichsrechner. Du wählst die wichtigsten Eckdaten (z. B. Leistungsumfang, Selbstbeteiligung, Laufzeit) und erhältst Tarife/Angebote. Wichtig: Der Preis ist nur ein Teil – entscheidend sind Leistungen, Ausschlüsse und Limits. Tipp: Erst Leistung passend einstellen, dann Preise vergleichen.'
    }, {
      id: 'q3',
      q: 'Kostet mich das etwas?',
      a: 'Die Nutzung der Website und der Rechner ist kostenlos. Wenn du eine persönliche Beratung/Einordnung möchtest, klären wir transparent, wie die Betreuung erfolgt.'
    }]
  }, {
    category: 'Beratung & Service',
    questions: [{
      id: 'q4',
      q: 'Warum Beratung – wenn ich schon vergleichen kann?',
      a: 'Vergleichen ist der erste Schritt. Beratung hilft vor allem bei: richtiger Leistungsauswahl (was ist wirklich wichtig?), Fallstricken in Bedingungen (Ausschlüsse, Sublimits, Obliegenheiten) und Einordnung deiner Situation (z. B. Beruf, Immobilie, Familie, Gesundheit). Wenn du willst, begleiten wir dich bis zur Umsetzung – wenn nicht, nutzt du die Infos einfach so.'
    }, {
      id: 'q5',
      q: 'Bist du Versicherungsmakler – und wie verdienst du?',
      a: 'Ja. Axentra Advisory arbeitet als Versicherungsmakler. Üblicherweise erfolgt die Vergütung über die vom Versicherer gezahlte Vermittlungs-/Betreuungsprovision, wenn ein Vertrag zustande kommt. Für dich entstehen dadurch in der Regel keine zusätzlichen Kosten. Transparenz ist wichtig: Wenn in einem Einzelfall abweichende Vergütungsmodelle (z. B. Honorar) sinnvoll sind, sprechen wir das vorher offen ab.'
    }, {
      id: 'q6',
      q: 'Kann ich bestehende Verträge prüfen lassen?',
      a: 'Ja. Du kannst bestehende Policen/Beiträge/Unterlagen einreichen und wir prüfen, ob Leistungen, Preis und Bedingungen noch zu dir passen – und wo Optimierung möglich ist.'
    }]
  }, {
    category: 'Datenschutz & Kontakt',
    questions: [{
      id: 'q7',
      q: 'Welche Daten verarbeitest du bei einer Anfrage?',
      a: 'Wenn du das Beratungsformular nutzt, verarbeiten wir die Angaben, die du dort machst (z. B. Name, E-Mail, Nachricht; Telefon/PLZ optional). Details findest du in der Datenschutzerklärung.'
    }, {
      id: 'q8',
      q: 'Wie schnell bekomme ich eine Rückmeldung?',
      a: 'In der Regel melden wir uns zeitnah zurück. Wenn du eine konkrete Frist hast, schreib sie einfach in die Nachricht – dann versuchen wir, das zu berücksichtigen.'
    }]
  }];
  return <div className="min-h-screen bg-sp-light dark:bg-slate-950 transition-colors duration-300" data-id="element-619">
      {/* Hero Section */}
      <div className="relative overflow-hidden bg-gradient-to-br from-slate-50 via-blue-50/30 to-sky-50/40 dark:from-slate-950 dark:via-slate-900 dark:to-blue-950/20 py-20 lg:py-28 border-b border-slate-200 dark:border-slate-800" data-id="element-620">
        <AnimatedGridPattern numSquares={30} maxOpacity={0.15} duration={3} repeatDelay={1} className="[mask-image:radial-gradient(600px_circle_at_center,white,transparent)] inset-x-0 inset-y-[-30%] h-[200%] skew-y-12" data-id="element-621" />

        <div className="container mx-auto px-4 text-center relative z-10" data-id="element-622">
          <div className="inline-flex items-center gap-2 rounded-full bg-blue-50 dark:bg-blue-950/30 px-3 py-1.5 text-xs font-medium text-blue-700 dark:text-blue-400 border border-blue-100 dark:border-blue-900 mb-6" data-id="element-623">
            <HelpCircle className="h-3.5 w-3.5" data-id="element-624" />
            <span data-id="element-625">Support Center</span>
          </div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 dark:text-white mb-6 tracking-tight" data-id="element-626">
            Häufig gestellte Fragen
          </h1>
          <p className="text-xl text-slate-600 dark:text-slate-300 max-w-2xl mx-auto leading-relaxed mb-10" data-id="element-627">
            Hier findest du Antworten auf die wichtigsten Fragen rund um
            SmartProtect24.
          </p>

          {/* Search Bar */}
          <div className="max-w-xl mx-auto relative" data-id="element-628">
            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none" data-id="element-629">
              <Search className="h-5 w-5 text-slate-400" data-id="element-630" />
            </div>
            <input type="text" placeholder="Wonach suchst du?" className="w-full pl-12 pr-4 py-4 rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-white shadow-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-631" />
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-20 max-w-4xl" data-id="element-632">
        <div className="space-y-16" data-id="element-633">
          {faqs.map((section, idx) => <div key={idx} data-id="element-634">
              <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-8 flex items-center gap-3" data-id="element-635">
                <div className="h-8 w-1 bg-blue-600 rounded-full" data-id="element-636"></div>
                {section.category}
              </h2>
              <div className="space-y-4" data-id="element-637">
                {section.questions.map(item => <div key={item.id} className={`group bg-white dark:bg-slate-900 rounded-xl border border-subtle dark:border-slate-800 overflow-hidden transition-all duration-300 ${openIndex === item.id ? 'shadow-md ring-1 ring-blue-100 dark:ring-blue-900' : 'shadow-sm hover:border-blue-200 dark:hover:border-slate-700'}`} data-id="element-638">
                    <button onClick={() => toggleFAQ(item.id)} className="w-full text-left px-6 py-5 flex items-center justify-between gap-4" data-id="element-639">
                      <span className="text-lg font-semibold text-slate-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors" data-id="element-640">
                        {item.q}
                      </span>
                      <div className={`flex-shrink-0 h-8 w-8 rounded-full bg-slate-50 dark:bg-slate-800 flex items-center justify-center transition-all duration-300 ${openIndex === item.id ? 'bg-blue-100 dark:bg-blue-900/30 rotate-180' : 'group-hover:bg-blue-50 dark:group-hover:bg-slate-700'}`} data-id="element-641">
                        <ChevronDown className={`h-5 w-5 transition-colors ${openIndex === item.id ? 'text-blue-600 dark:text-blue-400' : 'text-slate-400'}`} data-id="element-642" />
                      </div>
                    </button>
                    <div className={`px-6 text-slate-600 dark:text-slate-400 leading-relaxed overflow-hidden transition-all duration-300 ease-in-out ${openIndex === item.id ? 'max-h-48 pb-6 opacity-100' : 'max-h-0 opacity-0'}`} data-id="element-643">
                      {item.a}
                    </div>
                  </div>)}
              </div>
            </div>)}
        </div>
      </div>

      <CTASection title="Noch Fragen offen?" description="Unser Support-Team hilft Ihnen gerne weiter." primaryCtaText="Kontakt aufnehmen" onPrimaryClick={() => onNavigate('kontakt')} data-id="element-644" />
    </div>;
}